import { RoomPreferencesOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from "@mui/material";
import type { NotificationChannels } from "@repo/types";
import { useState } from "react";

import { DEFAULT_PREFERENCES } from "@/utils/constants/notification";

export type NotificationPreferences = Required<NotificationChannels>;

type ManagePreferencesProps = {
  initialPreferences?: NotificationPreferences;
  onSave?: (preferences: NotificationPreferences) => void;
};

export function ManagePreferences({
  initialPreferences = DEFAULT_PREFERENCES,
  onSave,
}: ManagePreferencesProps) {
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences>(initialPreferences);

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    onSave?.(preferences);
    setOpen(false);
  };

  const handleCancel = () => {
    setPreferences(initialPreferences);
    setOpen(false);
  };

  return (
    <>
      <Button
        startIcon={<RoomPreferencesOutlined />}
        onClick={() => setOpen(true)}
        variant="outlined"
        size="small"
        sx={{ textTransform: "none" }}
      >
        Preferences
      </Button>

      <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Notification Preferences</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose how you want to receive notifications
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.pushNotifications}
                    onChange={() => handleToggle("pushNotifications")}
                    color="primary"
                  />
                }
                label="Push Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.emailNotifications}
                    onChange={() => handleToggle("emailNotifications")}
                    color="primary"
                  />
                }
                label="Email Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={preferences.smsNotifications}
                    onChange={() => handleToggle("smsNotifications")}
                    color="primary"
                  />
                }
                label="SMS Notifications"
              />
            </FormGroup>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
